import { format } from "date-fns";
import type { DashboardUserDTO } from "../../dtos/DashboardUserDTO";
import type { UnderwritingResponse } from "../../pages/CreditPolicy/interface";
import type { PaymentAdviceType } from "../../pages/Disbursement/constants";
import type { CoApplicantKYCResponse } from "../../pages/LoanApplicationDetails/interface";
import type {
  LoansListParams,
  LoansListResponse
} from "../../pages/LoanApplications/interfaces";

import { Get, Post, Put, Delete } from "../../shared/services/Fetch";
import {
  getCurrentLender,
  getCurrentOrganisation,
  noHeadersResponse
} from "../../shared/utils/Global";
import { LENDER_NAMES } from "../../shared/utils/constants";
import { notify } from "../../shared/context/AlertContext";
import type { FinBoxResponse } from "../../shared/services/APIResponse";
import { authService } from "../apis";
import type {
  BureauDataInterface,
  CibilDataInterface,
  DocInfo,
  ServiceMethodArgs
} from "./interfaces";
import { getStoredProgrammeType } from "src/shared/utils/platformAuth";

const useV2 = () => {
  const currentLender = getCurrentLender();
  const currentOrganisation = getCurrentOrganisation();

  return (
    currentLender === LENDER_NAMES.HFCL ||
    currentLender === LENDER_NAMES.MFL_LAP ||
    currentLender === LENDER_NAMES.ABFL_CO_LENDING ||
    currentOrganisation === LENDER_NAMES.HFCL ||
    currentOrganisation === LENDER_NAMES.MFL_LAP
  );
};

export class LoanApplicationService {
  constructor() {}

  static async getLoanList({
    token,
    limit = 10,
    page = 1,
    statusText,
    searchType,
    searchQuery,
    filters = {},
    productType = "",
    sourceEntityIDs = [],
    dateFilter,
    to = null,
    from = null,
    pageName,
    dsaIDs = [],
    taskStatus,
    lenderIDs = [],
    workflowGroups
  }: ServiceMethodArgs<LoansListParams>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    const params = new URLSearchParams({ ...(filters as object) });
    params.append("limit", String(limit));
    params.append("page", String(page));
    let url = "getLoanList";

    if (statusText) {
      statusText
        .filter((s) => s !== "TOTAL" && s !== "total")
        .map((s) => {
          params.append("statusText", s.toUpperCase());
        });
    }

    if (to && from) {
      params.append("to", format(to, "yyyy-MM-dd"));
      params.append("from", format(from, "yyyy-MM-dd"));
    }

    if (searchType && searchQuery) {
      params.append(searchType, searchQuery);
      url = "searchOnlyGetLoanList";
    }
    if (productType) {
      params.append("loanType", productType);
    }
    if (sourceEntityIDs?.length > 0) {
      sourceEntityIDs?.map((entityID) =>
        params.append("sourceEntityIDs", entityID)
      );
    }
    if (dsaIDs?.length > 0) {
      dsaIDs?.map((dsaID) => params.append("dsaID", dsaID));
    }
    if (lenderIDs?.length) {
      lenderIDs?.map((lenderID) => params.append("lenderIDs", lenderID));
    }

    if (taskStatus) {
      params.append("workflowFilter", taskStatus);
    }

    if (workflowGroups) {
      params.append("workflowGroups", workflowGroups);
    }

    params.append("filter", dateFilter);
    return await Get<LoansListResponse>(
      `/masterDashboard/${url}?${params.toString()}`,
      token || "",
      headers
    );
  }

  static async updateUserMetadata({
    payload,
    pageName,
    token
  }: ServiceMethodArgs<{
    payload: any;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return Post(`/lender/updateUserMetadata`, token, payload, headers);
  }

  static async getFilterCounts({
    page = 1,
    searchType = "",
    searchQuery = "",
    filters = {},
    statusText = [],
    token = "",
    limit = 10,
    dateFilter,
    productType = "",
    sourceEntityIDs = [],
    to = null,
    from = null,
    pageName,
    dsaIDs = [],
    taskStatus,
    lenderIDs = [],
    workflowGroups
  }: ServiceMethodArgs<LoansListParams>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    const params = new URLSearchParams({ ...(filters as object) });
    params.append("limit", String(limit));
    params.append("page", String(page));
    if (statusText) {
      statusText
        .filter((s) => s !== "TOTAL" && s !== "total")
        .map((s) => {
          params.append("statusText", s);
        });
    }
    if (searchType && searchQuery) {
      params.append(searchType, searchQuery);
    }
    if (to && from) {
      params.append("to", format(to, "yyyy-MM-dd"));
      params.append("from", format(from, "yyyy-MM-dd"));
    }
    if (productType) {
      params.append("loanType", productType);
    }
    if (sourceEntityIDs?.length > 0) {
      sourceEntityIDs?.map((entityID) =>
        params.append("sourceEntityIDs", entityID)
      );
    }
    if (dsaIDs?.length > 0) {
      dsaIDs?.map((dsaID) => params.append("dsaID", dsaID));
    }

    if (lenderIDs?.length) {
      lenderIDs?.map((lenderID) => params.append("lenderIDs", lenderID));
    }

    if (taskStatus) {
      params.append("workflowFilter", taskStatus);
    }

    if (workflowGroups) {
      params.append("workflowGroups", workflowGroups);
    }

    params.append("filter", dateFilter);

    const URL = `/masterDashboard/loanStatusAggregateCount?${params.toString()}`;
    return await Get<any>(URL, token, headers);
  }

  static async checkPincode({
    token,
    pageName,
    pincode,
    isLenderPlatform
  }: ServiceMethodArgs<{ pincode: string; isLenderPlatform: boolean }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    return await Get(
      `/${
        isLenderPlatform ? "lender" : "masterDashboard"
      }/checkPincode?pincode=${pincode}`,
      token,
      headers
    );
  }

  static async getNearestBranch({
    payload,
    token,
    pageName,
    isLenderPlatform
  }: ServiceMethodArgs<{ payload: any; isLenderPlatform: boolean }>) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    else return noHeadersResponse;

    return await Post(
      `${
        isLenderPlatform ? `/lender` : `/masterDashboard`
      }/lenderDropdownSearch`,
      token,
      payload,
      headers
    );
  }

  static async createCoApplicant({
    token,
    pageName,
    payload
  }: ServiceMethodArgs<{
    payload: {
      pan: string;
      area: string;
      mobile: string;
      loanApplicationID: string;
      isLenderDashboard: boolean;
      sourceEntityID: string;
    };
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    if (!payload?.isLenderDashboard) {
      headers?.append("source-entity-id", payload?.sourceEntityID);
      return await Put(
        `/masterDashboard/createCoApplicant`,
        token,
        payload,
        headers
      );
    }

    return await Put(`/lender/createCoApplicant`, token, payload, headers);
  }

  static async updateCoApplicant({
    token,
    pageName,
    payload
  }: ServiceMethodArgs<{
    payload: {
      loanApplicationID: string;
      pan: string;
      email: string;
      dob: string;
      gender: number;
      pincode: string;
      firstName: string;
      middleName: string;
      lastName: string;
      relation: string;
      addressLine1: string;
      addressLine2: string;
      city: string;
      state: string;
      area: string;
      isLenderDashboard: boolean;
      sourceEntityID: string;
    };
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    if (!payload?.isLenderDashboard) {
      headers?.append("source-entity-id", payload?.sourceEntityID);
      return await Post(
        `/masterDashboard/updateCoApplicant`,
        token,
        payload,
        headers
      );
    }
    return await Put(`/lender/updateCoApplicant`, token, payload, headers);
  }

  static async getLoanApplicationList(params: LoansListParams) {
    return await LoanApplicationService.getLoanList(params);
  }

  static async fetchDocumentListByCategory({
    pageName,
    token,
    payload,
    isPlatformDashboard,
    sourceEntityID
  }: {
    pageName: string;
    token: string;
    payload: any;
    isPlatformDashboard?: boolean;
    sourceEntityID?: string;
  }) {
    const headers = new Headers();
    headers.append("page-header", pageName);
    if (sourceEntityID) headers.append("source-entity-id", sourceEntityID);

    return await Post(
      `/${
        isPlatformDashboard ? "masterDashboard" : "lender"
      }/generic-dropdown-list`,
      token,
      payload,
      headers
    );
  }

  static async uploadAllDocs({
    pageName,
    token,
    payload,
    isPlatformDashboard,
    sourceEntityID
  }: {
    pageName: string;
    token: string;
    payload: any;
    isPlatformDashboard?: boolean;
    sourceEntityID?: string;
  }) {
    const headers = new Headers();
    headers.append("page-header", pageName);
    if (sourceEntityID) headers.append("source-entity-id", sourceEntityID);

    return await Post(
      `/${isPlatformDashboard ? "masterDashboard" : "lender"}/uploadDocs`,
      token,
      payload,
      headers
    );
  }

  static async getGenericDropdownList({
    pageName,
    token,
    payload,
    isPlatformDashboard
  }: {
    pageName: string;
    token: string;
    payload: any;
    isPlatformDashboard?: boolean;
  }) {
    const headers = new Headers();
    headers.append("page-header", pageName);

    return await Post(
      `/${
        isPlatformDashboard ? "masterDashboard" : "lender"
      }/generic-dropdown-list`,
      token,
      payload,
      headers
    );
  }

  static async getDropdownConfig({
    token,
    lenderID,
    pageName,
    dropdownType,
    dropdownName,
    isLenderDashboard,
    sourceEntityID
  }: ServiceMethodArgs<{
    lenderID: string;
    dropdownName: string;
    dropdownType: string;
    isLenderDashboard: boolean;
    sourceEntityID?: any;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    const queryString = new URLSearchParams({});
    queryString.append("dropdownType", dropdownType);
    queryString.append("dropdownName", dropdownName);
    queryString.append("lenderID", lenderID);

    if (!isLenderDashboard) {
      headers.append("source-entity-id", sourceEntityID);
      return await Get(
        `/masterDashboard/getDropdownConfig?${queryString}`,
        token,
        headers
      );
    }

    return await Get(
      `/lender/getDropdownConfig?${queryString}`,
      token,
      headers
    );
  }

  static async regenerateKYCZip({
    loanApplicationID,
    token
  }: ServiceMethodArgs<{ loanApplicationID: string }>) {
    return await Get(
      `/lender/regenerateKYCZip?loanApplicationID=${loanApplicationID}`,
      token
    );
  }
  static async regenrateKYCDocs({
    payload,
    token,
    pageName
  }: ServiceMethodArgs<{ payload: any; pageName: string }>) {
    const headers = new Headers();
    headers.append("page-header", pageName);
    return await Post(`/lender/regenrate-kyc-docs`, token, payload, headers);
  }

  static async invokeVKYC({
    token,
    pageName,
    payload
  }: ServiceMethodArgs<{
    payload: { loanApplicationID: string; userID: string };
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    return await Post(`/lender/invokeVKYC`, token, payload, headers);
  }

  static async updateAdditionalDoc<T>({
    formData,
    token,
    pageName
  }: ServiceMethodArgs<{ formData: FormData }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    return await Post<T>(
      `/lender/updateAdditionalDocs`,
      token,
      formData,
      headers
    );
  }

  static async updateAdditionalKYCDoc({
    formData,
    token,
    pageName
  }: ServiceMethodArgs<{ formData: FormData }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    return await Post(
      `/lender/updateAdditionalKYCDoc`,
      token,
      formData,
      headers
    );
  }

  static async bulkLoanClosure({
    formData,
    token,
    pageName
  }: ServiceMethodArgs<{ formData: FormData }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    return await Post(
      `/masterDashboard/bulkLoanClosure`,
      token,
      formData,
      headers,
      true
    );
  }

  static async sampleMarkLoanClosureInBulk({
    pageName,
    token
  }: ServiceMethodArgs<{}>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    return Get(`/masterDashboard/sampleMarkLoanClosureInBulk`, token, headers);
  }

  static async getVerifications<T>({
    pageName,
    token,
    payload
  }: {
    pageName: string;
    token: string;
    payload: any;
  }): Promise<FinBoxResponse<T>> {
    const headers = new Headers();

    if (pageName) headers.append("page-header", pageName);

    return await Post(`/lender/getTaskList`, token, payload, headers);
  }

  static async updateHunterDetails({
    token,
    payload,
    pageName
  }: ServiceMethodArgs<{
    payload: { userID: string; hunterApprovalStatus: string };
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    return await Post<FinBoxResponse<{ msg: string }>>(
      `/lender/updateHunterDetails`,
      token,
      payload,
      headers
    );
  }

  static async startCoApplicantApplication({
    token,
    payload,
    pageName,
    isPlatformDashboard,
    sourceEntityID
  }: ServiceMethodArgs<{
    payload: {
      mobile: string;
      loanApplicationID: string;
      redirectURL: string;
    };
    isPlatformDashboard?: boolean;
    sourceEntityID?: string;
  }>) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    if (sourceEntityID) headers.append("source-entity-id", sourceEntityID);

    return await Post<FinBoxResponse<{ msg: string }>>(
      `/${isPlatformDashboard ? "masterDashboard" : "lender"}/add-coapplicant`,
      token,
      payload,
      headers
    );
  }

  static async addNote({
    note,
    token,
    pageName
  }: ServiceMethodArgs<{ note: any }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    const response = await Post(
      `/masterDashboard/addNote`,
      token,
      {
        note,
        loanApplicationID: note.loanApplicationID
      },
      headers
    );

    return response;
  }

  static async cancelNACH({
    loanApplicationID,
    token,
    pageName
  }: {
    loanApplicationID: string;
    token: string;
    pageName: string;
  }) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Get(
      `/lender/cancelNACH?loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }

  static async updateSoftApprovalStatus<T>({
    loanApplicationID,
    pageName,
    token
  }: {
    loanApplicationID: string;
    pageName: string;
    token: string;
  }): Promise<FinBoxResponse<T>> {
    const headers = new Headers();

    if (pageName) {
      headers?.append("page-header", pageName);
    }

    const body = {
      loanApplicationID: loanApplicationID
    };

    return await Post<T>(
      `/lender/updateSoftApproveStatus`,
      token,
      body,
      headers
    );
  }

  static async retriggerCoAppBureau({
    token,
    pageName,
    payload,
    isPlatformDashboard
  }: ServiceMethodArgs<{ payload: any; isPlatformDashboard: boolean }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Post(
      `/${
        isPlatformDashboard ? "masterDashboard" : "lender"
      }/retriggerCoAppBureau`,
      token,
      payload,
      headers
    );
  }

  static async deleteCoApplicant({
    token,
    pageName,
    payload
  }: ServiceMethodArgs<{ payload: any }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    if (!payload?.isLenderDashboard) {
      headers?.append("source-entity-id", payload?.sourceEntityID);

      return await Put(
        `/masterDashboard/deleteCoApplicant`,
        token,
        payload,
        headers
      );
    }

    return await Put(`/lender/deleteCoApplicant`, token, payload, headers);
  }

  static async updateIndustryType({
    loanApplicationID,
    industry,
    subIndustry,
    riskClassification,
    riskCategorization,
    token,
    pageName,
    natureOfBusiness,
    margin
  }: {
    loanApplicationID: string;
    industry: string;
    subIndustry: string;
    riskClassification: string;
    riskCategorization: string;
    token: string;
    pageName: string;
    natureOfBusiness: string;
    margin: number;
  }) {
    const body = {
      loanApplicationID,
      industry,
      subIndustry,
      riskClassification,
      riskCategorization,
      natureOfBusiness,
      margin
    };
    const headers = new Headers();
    headers.append("page-header", pageName);
    return await Post(`/lender/updateBusinessIndustry`, token, body, headers);
  }

  static async sendLinkToUser({
    userID,
    token,
    pageName,
    sourceEntityID
  }: ServiceMethodArgs<{ userID: any; sourceEntityID: any }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
      headers.append("source-entity-id", sourceEntityID);
    } else {
      return noHeadersResponse();
    }

    const response = await Post(
      `/masterDashboard/sendSessionLinkToCustomer`,
      token,
      {
        userID: userID
      },
      headers
    );

    return response;
  }
  static async markNACHSuccess({
    token,
    payload,
    pageName
  }: ServiceMethodArgs<{
    payload: { loanApplicationID: string; umrn: string };
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    return await Post(`/lender/markNACHSuccess`, token, payload, headers);
  }
  static async updateCoApplicantKYCDoc({
    token,
    pageName,
    payload
  }: {
    token: string;
    pageName: string;
    payload: any;
  }) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    return await Post<FinBoxResponse<{ msg: string }>>(
      `/lender/updateCoApplicantKYCDoc`,
      token,
      payload,
      headers
    );
  }

  static async updateCoApplicantDocReviewStatus({
    formData,
    token,
    pageName
  }: ServiceMethodArgs<{ formData: FormData }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    return await Put(
      `/lender/updateCoApplicantDocReviewStatus`,
      token,
      formData,
      headers
    );
  }

  static async handleEvent({
    payload,
    token,
    pageName,
    isLenderDashboard = true,
    sourceEntityID
  }: ServiceMethodArgs<{
    payload: any;
    pageName: string;
    isLenderDashboard: boolean;
    sourceEntityID?: string;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    if (sourceEntityID) {
      headers.append("source-entity-id", sourceEntityID);
    }

    return await Post(
      `${isLenderDashboard ? "/lender" : "/masterDashboard"}/handle-event`,
      token,
      payload,
      headers
    );
  }

  static async fetchGraphqlData({
    token,
    pageName,
    payload,
    sourceEntityID
  }: ServiceMethodArgs<{
    payload: {
      dashboardType: "masterDashboard" | "lender";
      [key: string]: any;
    };
    token: string;
    pageName: string;
    isLenderDashboard?: boolean;
    sourceEntityID?: string;
  }>) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    else return noHeadersResponse();
    if (sourceEntityID) headers.append("source-entity-id", sourceEntityID);

    return await Post(
      payload?.dashboardType === "masterDashboard"
        ? `/masterDashboard/fetch-graphql-data`
        : `/lender/fetch-graphql-data`,
      token,
      payload,
      headers
    );
  }

  static async addReferencesForUser({
    token,
    payload,
    pageName,
    isLenderDashboard
  }: ServiceMethodArgs<{
    payload: {
      name: string;
      pan: string;
      mobile: string;
      relation: string;
      loanApplicationID: string;
      isLenderPlatform: boolean;
    };
    isLenderDashboard?: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    const url = isLenderDashboard
      ? `/lender/addReferencesForUser`
      : `/masterDashboard/addReferencesForUser`;
    return await Put(url, token, payload, headers);
  }

  static async getNotes({
    loanApplicationID,
    token,
    pageName
  }: ServiceMethodArgs<{ loanApplicationID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Get(
      `/masterDashboard/notes?loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }

  static async getLenderAdditionalDocsOpt({
    token,
    pageName,
    serviceType,
    loanApplicationID
  }: ServiceMethodArgs<{ loanApplicationID: string; serviceType: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }

    return await Get(
      `/lender/dropdownL2?loanApplicationID=${loanApplicationID}&serviceType=${serviceType}`,
      token,
      headers
    );
  }

  static async getAdditionalDocsOpt({
    token,
    pageName,
    serviceType,
    loanApplicationID
  }: ServiceMethodArgs<{ loanApplicationID: string; serviceType: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Get(
      `/masterDashboard/dropdownL2?serviceType=${serviceType}&loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }

  static async pushLenderAdditionalDocs({
    token,
    pageName,
    payload
  }: ServiceMethodArgs<{
    payload: { loanApplicationID: string; docsInfo: DocInfo };
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    return await Post(`/lender/pushAdditionalDocs`, token, payload, headers);
  }

  static async pushAdditionalDocs({
    token,
    pageName,
    payload
  }: ServiceMethodArgs<{
    payload: { loanApplicationID: string; docsInfo: DocInfo };
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    return await Post(
      `/masterDashboard/pushAdditionalDocs`,
      token,
      payload,
      headers
    );
  }

  static async getCibilPdfReport({
    userID,
    token,
    pageName
  }: ServiceMethodArgs<{ userID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Get(
      `/masterDashboard/getCibilPDFReport?userID=${userID}`,
      token,
      headers
    );
  }

  static async archiveUser({
    userID,
    remark,
    token,
    pageName
  }: ServiceMethodArgs<{ userID: string; remark: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    const searchParams = new URLSearchParams({ userID, remark });

    return await Get(
      `/masterDashboard/archiveUser?${searchParams.toString()}`,
      token,
      headers
    );
  }

  static async getCIBILReportsExcel({
    userID,
    token,
    pageName
  }: ServiceMethodArgs<{ userID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Get(
      `/masterDashboard/cibilReportsExcel?userID=${userID}`,
      token,
      headers
    );
  }

  static async getMargin<T>({
    pageName,
    token,
    loanApplicationID,
    industry,
    subIndustry,
    natureOfBusiness
  }: ServiceMethodArgs<{
    loanApplicationID: string;
    industry: string;
    subIndustry: string;
    natureOfBusiness: string;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    return await Get<T>(
      `/lender/getMargin?loanApplicationID=${loanApplicationID}&industry=${encodeURIComponent(
        industry
      )}&subIndustry=${encodeURIComponent(
        subIndustry
      )}&natureOfBusiness=${natureOfBusiness}`,
      token,
      headers
    );
  }

  static async getLoanOffer({
    loanApplicationID,
    token,
    pageName
  }: ServiceMethodArgs<{ loanApplicationID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    return await Get(
      `/masterDashboard/getLoanOffer?loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }

  static async getOfferNegotiationEMI({
    token,
    payload,
    pageName,
    isLenderDashboard
  }: ServiceMethodArgs<{
    payload: {
      amount: number;
      tenure: number;
      interest: number;
      loanApplicationID: string;
    };
    isLenderDashboard: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    else return noHeadersResponse();

    const url = isLenderDashboard ? "/lender" : "/masterDashboard";

    return await Post(url + `/getOfferNegotiationEMI`, token, payload, headers);
  }

  static async triggerFieldInvestigation({
    token,
    pageName,
    payload
  }: ServiceMethodArgs<{
    payload: { loanApplicationID: string; userID: string; userType: string };
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    return await Post(
      `/lender/trigger-field-investigation`,
      token,
      payload,
      headers
    );
  }

  static async getOfferNegotiationWFStatus({
    loanApplicationID,
    token,
    pageName
  }: ServiceMethodArgs<{ loanApplicationID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    return await Get(
      `/masterDashboard/getOfferNegotiationWFStatus?loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }

  static async getLoanTermDeviation({
    token,
    pageName,
    loanApplicationID
  }: ServiceMethodArgs<{ loanApplicationID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Get(
      `/masterDashboard/getOfferNegotiation?loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }

  static async saveLoanTermDeviation({
    token,
    payload,
    pageName,
    loanApplicationId
  }: ServiceMethodArgs<{ payload: any; loanApplicationId: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    const urlSearchParams = new URLSearchParams();

    if (loanApplicationId) {
      urlSearchParams.append("loanApplicationId", loanApplicationId);
    }

    const response = await Post(
      `/masterDashboard/addOfferNegotiation`,
      token,
      payload,
      headers
    );
    return response;
  }

  static async getLoanDetails({
    token,
    pageName,
    loanApplicationID
  }: ServiceMethodArgs<{ loanApplicationID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Get(
      `/masterDashboard/getLoanDetails?loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }

  static async getCoApplicants({
    token,
    pageName,
    loanApplicationID,
    isLenderDashboard
  }: ServiceMethodArgs<{
    loanApplicationID: string;
    isLenderDashboard: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    const queryString = new URLSearchParams({});
    queryString.append("loanApplicationID", loanApplicationID);

    return await Get(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/getCoApplicants?${queryString}`,
      token,
      headers
    );
  }

  static async getSalesDisposition({
    token,
    userId,
    pageName,
    sourceEntityId,
    loanApplicationID
  }: ServiceMethodArgs<{
    userId: string;
    sourceEntityId: string;
    loanApplicationID: string;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    return await Get(
      `/masterDashboard/getSalesDisposition?sourceEntityId=${sourceEntityId}${
        userId ? `&userId=${userId}` : ""
      }&loanApplicationId=${loanApplicationID}`,
      token,
      headers
    );
  }

  static async getSalesDispositionStatus({
    token,
    pageName,
    sourceEntityId
  }: ServiceMethodArgs<{ sourceEntityId: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    return await Get(
      `/masterDashboard/getSalesDispositionConfig?sourceEntityId=${sourceEntityId}`,
      token,
      headers
    );
  }

  static async saveSalesDisposition({
    data,
    token,
    pageName,
    loanApplicationId
  }: ServiceMethodArgs<{ data: any; loanApplicationId: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    const urlSearchParams = new URLSearchParams();

    if (loanApplicationId) {
      urlSearchParams.append("loanApplicationId", loanApplicationId);
    }

    const response = await Post(
      `/masterDashboard/addSalesDisposition?${urlSearchParams.toString()}`,
      token,
      data,
      headers
    );
    return response;
  }

  static async uploadDocs({
    token,
    formData,
    pageName,
    isLenderPlatform,
    payload
  }: ServiceMethodArgs<{
    formData?: any;
    isLenderPlatform: boolean;
    payload?: any;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    const url = isLenderPlatform
      ? `/lender/uploadDocs`
      : `/masterDashboard/uploadDocs`;

    if (payload) {
      return await Post(url, token, payload, headers);
    }

    return await Post(url, token, formData, headers);
  }

  static async getKYCDetails({
    loanApplicationID,
    token,
    pageName
  }: ServiceMethodArgs<{ loanApplicationID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Get(
      `/masterDashboard/getKYCDetails?loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }

  static async getLoanKYC({
    loanApplicationID,
    token,
    pageName,
    isLenderDashboard
  }: ServiceMethodArgs<{
    loanApplicationID: string;
    isLenderDashboard: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    const url = isLenderDashboard ? "/lender" : "/masterDashboard";
    return await Get(
      url + `/getLoanKYC?loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }

  static async retriggerKYCEngine({
    loanApplicationID,
    token,
    pageName,
    isLenderDashboard
  }: ServiceMethodArgs<{
    loanApplicationID: string;
    isLenderDashboard: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    const url = isLenderDashboard
      ? `/lender/retriggerKYCEngine?loanApplicationID=`
      : `/masterDashboard/retriggerKYCEngine?loanApplicationID=`;
    return await Get(url + loanApplicationID, token, headers);
  }

  static async getBankDetails({
    loanApplicationID,
    sourceEntityID,
    token,
    pageName,
    isLenderPlatform
  }: ServiceMethodArgs<{
    loanApplicationID: string;
    sourceEntityID: string;
    isLenderPlatform: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    const params = new URLSearchParams();
    params.append("loanApplicationID", loanApplicationID);
    params.append("sourceEntityID", sourceEntityID);

    const url = isLenderPlatform
      ? `/lender/getBankDetails?loanApplicationID=${loanApplicationID}`
      : `/masterDashboard/getBankDetails?${params}`;

    return await Get(url, token, headers);
  }

  static async changeAccountName({
    data,
    token,
    pageName
  }: ServiceMethodArgs<{ data: any }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Post(
      `/masterDashboard/changeAccountName`,
      token,
      data,
      headers,
      true
    );
  }

  static async updateKYCDoc({
    formData,
    token,
    pageName,
    isLenderDashboard
  }: ServiceMethodArgs<{ formData: FormData; isLenderDashboard: boolean }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    const url = isLenderDashboard
      ? `/lender/updateKYCDoc`
      : `/masterDashboard/updateKYCDoc`;
    return await Post(url, token, formData, headers);
  }

  static async getLoanActivity({
    loanApplicationID,
    token,
    page = 1,
    pageName,
    isLenderDashboard,
    userID
  }: ServiceMethodArgs<{
    loanApplicationID: string;
    page: number;
    isLenderDashboard: boolean;
    userID?: string;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    const url = isLenderDashboard ? "/lender" : "/masterDashboard";
    const userIDParam = userID ? `&userID=${userID}` : "";

    return await Get(
      url +
        `/loanActivity?loanApplicationID=${loanApplicationID}${userIDParam}&page=${page}&limit=8`,
      token,
      headers
    );
  }

  static async approveLoanKYC({
    loanApplicationID,
    token,
    isDoubleName,
    pageName
  }: ServiceMethodArgs<{ isDoubleName: boolean; loanApplicationID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Post(
      "/masterDashboard/approveLoanKyc",
      token,
      {
        loanApplicationID,
        isDoubleName
      },
      headers
    );
  }

  static async rejectLoan({
    loanApplicationID,
    reason,
    token,
    pageName
  }: ServiceMethodArgs<{ reason: string; loanApplicationID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Post(
      `/masterDashboard/rejectLoan`,
      token,
      {
        loanApplicationID,
        rejectReason: reason
      },
      headers
    );
  }

  static async revokeDeleteRequest({
    token,
    userID,
    pageName
  }: ServiceMethodArgs<{ userID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Post(
      "/masterDashboard/revokeDelete",
      token,
      {
        userID
      },
      headers
    );
  }

  static async bypassBRE({
    token,
    userID,
    reason,
    pageName
  }: ServiceMethodArgs<{ userID: string; reason: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Post(
      "/masterDashboard/bypassBRE",
      token,
      {
        userID,
        reason
      },
      headers
    );
  }

  static async cancelApplication({
    reason,
    loanApplicationID,
    token,
    pageName
  }: ServiceMethodArgs<{
    reason: string;
    loanApplicationID: string;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Post(
      "/masterDashboard/cancelLoan",
      token,
      {
        loanApplicationID,
        cancellationReason: reason
      },
      headers
    );
  }

  static async cancelUser({
    token,
    pageName,
    payload,
    isLenderDashboard
  }: ServiceMethodArgs<{
    payload: { mobile: string; sourceEntityID: string; userID: string };
    isLenderDashboard: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Post(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/cancel-user`,
      token,
      payload,
      headers
    );
  }

  static async getWaitState({
    token,
    loanApplicationID,
    pageName,
    isLenderPlatform
  }: ServiceMethodArgs<{
    loanApplicationID: string;
    isLenderPlatform: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    const url = isLenderPlatform
      ? "/lender/getWaitState?loanApplicationID"
      : "/masterDashboard/getWaitState?loanApplicationID";

    return await Get(url + `=${loanApplicationID}`, token, headers);
  }

  static async addCoApplicantKYCDoc({
    token,
    pageName,
    payload
  }: {
    token: string;
    pageName: string;
    payload: any;
  }) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    return await Post<FinBoxResponse<{ msg: string }>>(
      `/lender/addCoApplicantKYCDoc`,
      token,
      payload,
      headers
    );
  }

  static async downloadKycPdf({
    loanApplicationID,
    token,
    pageName
  }: ServiceMethodArgs<{ loanApplicationID: string; pageName: string }>) {
    const headers = new Headers();
    headers.append("page-header", pageName);
    return await Get(
      `/lender/getEKYCPDF?loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }

  static async cancelEKYC({
    formData,
    token
  }: ServiceMethodArgs<{ formData: any }>) {
    return await Post(`/lender/cancelEKYC`, token, formData);
  }

  static async updateName<T>({
    name,
    userID,
    token,
    pageName,
    isLenderPlatform
  }: ServiceMethodArgs<{
    name: string;
    userID: string;
    isLenderPlatform: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    const url = isLenderPlatform
      ? "/lender/updateName"
      : "/masterDashboard/updateName";

    return await Post<T>(
      url,
      token,
      {
        name: name,
        userID: userID
      },
      headers
    );
  }

  static async updateFathersName<T>({
    fathersName,
    loanApplicationID,
    token,
    pageName,
    isLenderPlatform
  }: ServiceMethodArgs<{
    fathersName: string;
    loanApplicationID: string;
    isLenderPlatform: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    const url = isLenderPlatform
      ? "/lender/updateFathersName"
      : "/masterDashboard/updateFathersName";

    return await Post<T>(
      url,
      token,
      {
        fathersName,
        loanApplicationID
      },
      headers
    );
  }

  static async updateGender<T>({
    gender,
    loanApplicationID,
    token,
    pageName,
    isLenderPlatform
  }: ServiceMethodArgs<{
    gender: string;
    loanApplicationID: string;
    isLenderPlatform: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    const url = isLenderPlatform
      ? "/lender/updateGender"
      : "/masterDashboard/updateGender";

    return await Post<T>(
      url,
      token,
      {
        gender,
        loanApplicationID
      },
      headers
    );
  }

  static async updateDob<T>({
    dob,
    token,
    userID,
    pageName,
    isLenderPlatform
  }: ServiceMethodArgs<{
    dob: Date;
    userID: string;
    isLenderPlatform: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    const url = isLenderPlatform
      ? "/lender/updateDOB"
      : "/masterDashboard/updateDOB";
    const keyName: string = isLenderPlatform ? "loanApplicationID" : "userID";
    return await Post<T>(
      url,
      token,
      {
        dob: format(dob, "yyyy-MM-dd"),
        [keyName]: userID
      },
      headers
    );
  }

  static async updateCurrentAddress<T>({
    token,
    line1,
    line2,
    city,
    state,
    pincode,
    area,
    landmark = "",
    loanApplicationID,
    IsCurrentAndPermanentSame,
    pageName,
    isLenderPlatform
  }: ServiceMethodArgs<{
    line1: string;
    line2: string;
    city: string;
    state: string;
    pincode: string;
    area?: string;
    landmark?: string;
    loanApplicationID: string;
    IsCurrentAndPermanentSame: boolean;
    isLenderPlatform: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    const url = isLenderPlatform
      ? "/lender/updateCurrentAddress"
      : "/masterDashboard/updateCurrentAddress";

    const payload = {
      line1,
      line2,
      city,
      state,
      pincode,
      area,
      landmark,
      loanApplicationID,
      IsCurrentAndPermanentSame
    } as any;

    return await Post<T>(url, token, payload, headers);
  }

  static async updatePermanentAddress<T>({
    token,
    line1,
    line2,
    city,
    state,
    pincode,
    loanApplicationID,
    pageName,
    isLenderPlatform,
    nearestBranch,
    branchCode
  }: ServiceMethodArgs<{
    line1: string;
    line2: string;
    city: string;
    state: string;
    pincode: string;
    loanApplicationID: string;
    isLenderPlatform: boolean;
    nearestBranch?: string;
    branchCode?: string;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    const url = isLenderPlatform
      ? "/lender/updatePermanentAddress"
      : "/masterDashboard/updatePermanentAddress";
    return await Post<T>(
      url,
      token,
      {
        line1,
        line2,
        city,
        state,
        pincode,
        loanApplicationID,
        nearestBranch,
        branchCode
      },
      headers
    );
  }

  static async updateAggregateExposure<T>({
    aggregateExposureData,
    userID,
    token,
    identifier,
    pageName,
    isLenderPlatform,
    sourceEntityID
  }: ServiceMethodArgs<{
    aggregateExposureData: any;
    userID: string;
    identifier?: string;
    isLenderPlatform: boolean;
    sourceEntityID: string;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", "Loan Application Business Details Tab");
    } else {
      return noHeadersResponse();
    }

    if (sourceEntityID) {
      headers.append("source-entity-id", sourceEntityID);
    }
    const url = isLenderPlatform
      ? "/lender/updateUserMetadata"
      : "/masterDashboard/updateUserMetadata";

    const payload = {
      userID: userID,
      identifier: identifier || "business",
      metaData: {
        aggregateExposure: {
          lei: aggregateExposureData?.lei || "",
          isAggregateExposurePresent:
            aggregateExposureData?.isAggregateExposurePresent || ""
        }
      }
    };

    return await Post<T>(url, token, payload, headers);
  }

  static async updateOfficeAddress<T>({
    addressObject,
    userID,
    token,
    identifier,
    pageName,
    isLenderPlatform
  }: ServiceMethodArgs<{
    addressObject: any;
    userID: string;
    identifier?: string;
    isLenderPlatform: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", "Loan Application Business Details Tab");
    } else {
      return noHeadersResponse();
    }
    const url = isLenderPlatform
      ? "/lender/updateUserMetadata"
      : "/masterDashboard/updateBusinessAddress";

    const payload: any = {
      userID: userID,
      identifier: identifier || "user",
      metaData: addressObject
    };

    return await Post<T>(url, token, payload, headers);
  }

  static async updateAdditionalBusinessAddress<T>({
    addressObject,
    userID,
    token,
    identifier,
    pageName,
    addressCategory,
    isLenderPlatform,
    businessAddressType
  }: ServiceMethodArgs<{
    addressObject: any;
    userID: string;
    identifier?: string;
    isLenderPlatform: boolean;
    addressCategory: "";
    businessAddressType: any;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", "Loan Application Business Details Tab");
    } else {
      return noHeadersResponse();
    }

    const url = isLenderPlatform
      ? "/lender/updateUserMetadata"
      : "/masterDashboard/updateBusinessAddress";

    const payload: any = {
      userID: userID,
      identifier: identifier || "business",
      metaData: addressObject
    };
    if (payload.identifier === "business") {
      payload.metaData.additionalBusinessAddress["businessAddressType"] =
        businessAddressType;
      payload.metaData.additionalBusinessAddress["addressCategory"] =
        addressCategory;
    } else {
      payload.metaData.additionalPermanentAddress[
        "additionalPermanentAddressType"
      ] = businessAddressType;
    }

    return await Post<T>(url, token, payload, headers);
  }

  static async updateEmployerNameOthers<T>({
    employerNameOthers,
    userID,
    token,
    pageName,
    isLenderPlatform,
    identifier
  }: ServiceMethodArgs<{
    userID: string;
    isLenderPlatform: boolean;
    employerNameOthers: string;
    identifier: string;
    token: string;
  }>) {
    const headers = new Headers();
    if (pageName)
      headers.append("page-header", "Loan Application Application Form Tab");
    else return noHeadersResponse();

    const url = isLenderPlatform
      ? "/lender/updateUserMetadata"
      : "/masterDashboard/updateUserMetadata";

    const payload: any = {
      metadata: { employerNameOthers },
      userID,
      identifier
    };

    return await Post<T>(url, token, payload, headers);
  }

  static async updateBusinessAddress<T>({
    line1,
    line2,
    city,
    state,
    pincode,
    userID,
    token,
    area,
    businessAddressType,
    pageName,
    isLenderPlatform,
    nearestBranch,
    branchCode
  }: ServiceMethodArgs<{
    line1: string;
    line2: string;
    city: string;
    state: string;
    pincode: string;
    userID: string;
    area?: string;
    businessAddressType: string;
    isLenderPlatform: boolean;
    nearestBranch: string;
    branchCode: string;
  }>) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    else return noHeadersResponse();

    const url = isLenderPlatform
      ? "/lender/updateBusinessAddress"
      : "/masterDashboard/updateBusinessAddress";

    const payload = {
      line1,
      line2,
      city,
      state,
      pincode,
      userID,
      area,
      businessAddressType,
      nearestBranch,
      branchCode
    } as any;

    return await Post<T>(url, token, payload, headers);
  }

  static async updateBusinessDetails<T>({
    token,
    firmName,
    constitution,
    last12MonthsSale,
    dateOfIncorporation,
    userID,
    businessPAN,
    pageName,
    sourceEntityID,
    businessCategory,
    isLenderPlatform,
    natureOfBusiness,
    margin,
    subIndustry,
    industry
  }: ServiceMethodArgs<{
    userID: string;
    firmName: string;
    constitution: string;
    last12MonthsSale: string;
    dateOfIncorporation: Date;
    businessPAN: string;
    businessCategory: string;
    sourceEntityID?: string;
    isLenderPlatform?: any;
    natureOfBusiness: string;
    margin: number;
    subIndustry: string;
    industry: string;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    if (sourceEntityID) {
      headers.append("source-entity-id", sourceEntityID);
    }
    const formData: any = {
      firmName,
      constitution,
      last12MonthsSale,
      dateOfIncorporation: format(dateOfIncorporation, "yyyy-MM-dd"),
      userID,
      businessCategory,
      natureOfBusiness,
      margin,
      subIndustry,
      industry
    };

    if (constitution !== "Proprietorship") {
      formData.businessPAN = businessPAN;
    }

    const url = isLenderPlatform
      ? "/lender/updateBusinessDetails"
      : "/masterDashboard/updateBusinessDetails";
    return await Post<T>(url, token, formData, headers);
  }

  static async applyNewLoan<T>({
    token,
    userID,
    pageName
  }: ServiceMethodArgs<{
    userID: string;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Post<T>(
      "/masterDashboard/applyNewLoan",
      token,
      {
        userID
      },
      headers
    );
  }

  static async getBusinessDetails<T>({
    token,
    userID,
    pageName,
    isLenderPlatform
  }: ServiceMethodArgs<{
    userID: string;
    isLenderPlatform: false;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    const url = isLenderPlatform
      ? `/lender/getBusinessDetails?loanApplicationID=${userID}`
      : `/masterDashboard/getBusinessDetails?userID=${userID}`;
    return await Get<T>(url, token, headers);
  }

  static async getGstinDoc<T>({
    token,
    userID,
    gstin,
    pageName,
    sourceEntityID
  }: ServiceMethodArgs<{
    userID: string;
    gstin: string;
    pageName: string;
    sourceEntityID?: string;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    if (sourceEntityID) headers.append("source-entity-id", sourceEntityID);

    return await Get<T>(
      `/masterDashboard/gstinDoc?userID=${userID}&gstin=${gstin}&regenerate=false`,
      token,
      headers
    );
  }

  static async rejectSingleDoc({
    token,
    loanApplicationID,
    kycDetailsID,
    rejectionReason,
    pageName,
    isLenderDashboard
  }: ServiceMethodArgs<{
    loanApplicationID: string;
    kycDetailsID: string;
    rejectionReason: string;
    isLenderDashboard: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    const url = isLenderDashboard
      ? `/lender/rejectSingleDoc`
      : `/masterDashboard/rejectSingleDoc`;
    return await Post(
      url,
      token,
      {
        loanApplicationID,
        loanKycDetailsID: kycDetailsID,
        rejectionReason
      },
      headers
    );
  }

  static async getBcRange({
    sourceEntityID,
    token,
    pageName
  }: ServiceMethodArgs<{ sourceEntityID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Get(
      `/masterDashboard/bcRange?sourceEntityID=${sourceEntityID}`,
      token,
      headers
    );
  }

  static async getBcStatus({
    userID,
    token,
    pageName
  }: ServiceMethodArgs<{ userID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Get(
      `/masterDashboard/bcStatus?userID=${userID}`,
      token,
      headers
    );
  }

  static async bankConnectCompleted({
    userID,
    entityID,
    token,
    pageName
  }: ServiceMethodArgs<{
    userID: string;
    entityID: string;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Post(
      `/masterDashboard/bankConnectCompleted`,
      token,
      {
        userID,
        entityID
      },
      headers
    );
  }

  static async sendAgreementToLender({
    token,
    loanApplicationID,
    pageName
  }: ServiceMethodArgs<{ loanApplicationID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Post(
      "/masterDashboard/sendSignAgreementDoc",
      token,
      {
        loanApplicationID
      },
      headers
    );
  }

  static async downloadBankConnectReport({
    userID,
    sourceEntityID,
    token,
    pageName
  }: ServiceMethodArgs<{ userID: string; sourceEntityID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Get(
      `/masterDashboard/getBankConnectReportV2?userID=${userID}&sourceEntityID=${sourceEntityID}`,
      token,
      headers
    );
  }

  static async getBankConnectReportByID({
    token,
    pageName,
    bankDetailsID,
    isLenderDashboard
  }: ServiceMethodArgs<{ bankDetailsID: string; isLenderDashboard: boolean }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Get(
      `/${
        isLenderDashboard ? "lender" : "masterDashboard"
      }/getBankConnectReportByID?bankDetailsID=${bankDetailsID}`,
      token,
      headers
    );
  }

  static async listBankConnectAttempts({
    userID,
    token,
    pageName
  }: ServiceMethodArgs<{ userID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Get(
      `/masterDashboard/listBankConnectAttempts?userID=${userID}`,
      token,
      headers
    );
  }

  static async getWorkflows({
    loanApplicationID,
    token,
    pageName
  }: ServiceMethodArgs<{ loanApplicationID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    const requestParams = new URLSearchParams();
    requestParams.append("resourceID", loanApplicationID);
    requestParams.append("resourceType", "loan_application");
    return await Get(
      `/masterDashboard/getWorkflows?${requestParams.toString()}`,
      token,
      headers
    );
  }

  static async approveBusinessKYC({
    token,
    pageName,
    loanApplicationID
  }: ServiceMethodArgs<{ loanApplicationID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    const payload = {
      loanApplicationID
    };

    const response = await Post(
      `/masterDashboard/approveBusinessDocs`,
      token,
      payload,
      headers
    );
    return response;
  }

  static async getCoApplicantsLoanKYC({
    loanApplicationID,
    token,
    pageName
  }: ServiceMethodArgs<{ loanApplicationID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    return await Get<CoApplicantKYCResponse>(
      `/masterDashboard/getCoApplicantsLoanKYC?loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }

  async getPermissions(tok: string) {
    try {
      const token = tok || authService.getToken();
      const headers = new Headers();
      headers.append("page-header", "Login");
      if (token) {
        const response = await Get<DashboardUserDTO>(
          `/masterDashboard/getPermissions`,
          token,
          headers
        );
        if (response.status) {
          return response.data;
        } else {
          notify({
            text: response.error,
            title: "Session Expired"
          });
          return null;
        }
      }

      notify({
        text: "User not logged in!",
        title: "Session Expired"
      });
      return null;
    } catch (err: any) {
      notify({
        title: "Permission Error",
        text: new Error(err).message
      });
      return null;
    }
  }

  static async onGenerateLinkClick(
    isLenderPlatform: boolean,
    pageName?: string,
    loanID?: string,
    token?: any,
    trackUserAction?: any
  ) {
    try {
      const headers = new Headers();
      if (pageName) headers.append("page-header", pageName);
      else return noHeadersResponse();

      const response = await Get<{ inviteURL: string }>(
        `/${
          isLenderPlatform ? "lender" : "masterDashboard"
        }/generateEmandateUrl?loanApplicationID=${loanID}&mandateType=`,
        token,
        headers
      );

      if (isLenderPlatform && trackUserAction) {
        trackUserAction("payments_generate_e_mandate_link", {
          loanApplicationID: loanID
        });
      }

      if (response.status) return response?.data?.inviteURL;
      else {
        notify({
          title: "Error",
          text: response.error
        });
        return null;
      }
    } catch (err: any) {
      notify({
        title: "Some error occurred",
        text: new Error(err).message
      });
      return null;
    }
  }

  static async generateEMandateLink(
    pageName: string,
    token: string,
    loanID: string,
    mandateType: string
  ) {
    try {
      const headers = new Headers();
      if (pageName) headers.append("page-header", pageName);
      else return noHeadersResponse();

      const response = await Get<{ inviteURL: string }>(
        `/lender/generateEmandateUrl?loanApplicationID=${loanID}&mandateType=${mandateType}`,
        token,
        headers
      );

      if (response) return response;
    } catch (err: any) {
      notify({
        title: "Some error occurred",
        text: new Error(err).message
      });
      return null;
    }
  }

  static async getPayments({
    loanApplicationID,
    token,
    pageName
  }: {
    loanApplicationID: string;
    token: string;
    pageName: string;
  }) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    else return noHeadersResponse();

    return await Get(
      `/masterDashboard/payments?loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }

  static async getVirtualAccounts({
    loanApplicationID,
    token,
    pageName
  }: {
    loanApplicationID: string;
    token: string;
    pageName: string;
  }) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    else return noHeadersResponse();

    return await Get(
      `/masterDashboard/getVirtualAccountDetails?loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }
  static async cancelENach<T>({
    token,
    loanID,
    isPlatformDashboard
  }: ServiceMethodArgs<{
    token: string;
    loanID: string;
    isPlatformDashboard: boolean;
  }>) {
    const headers = new Headers();

    const url = `/${
      isPlatformDashboard ? "masterDashboard" : "lender"
    }/execute-ui-action`;

    const payload: any = {
      loanApplicationID: loanID,
      sectionName: "enach_tab",
      resourceName: "enach_cancel"
    };

    return await Post<T>(url, token, payload, headers);
  }

  static async colendingAction<T>({
    token,
    pageName,
    payload,
    isLenderDashboard
  }: ServiceMethodArgs<{
    payload: any;
    pageName: string;
    isLenderDashboard: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName.trim());
    } else {
      return noHeadersResponse();
    }

    headers.append("page-header", pageName.trim());

    return await Post<T>(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/colending-action`,
      token,
      payload,
      headers
    );
  }

  static async updateCompanyName<T>({
    companyName,
    userID,
    token,
    pageName,
    isLenderPlatform,
    identifier
  }: ServiceMethodArgs<{
    userID: string;
    isLenderPlatform: boolean;
    companyName: string;
    identifier: string;
    token: string;
  }>) {
    const headers = new Headers();
    if (pageName)
      headers.append("page-header", "Loan Application Application Form Tab");
    else return noHeadersResponse();

    const url = isLenderPlatform
      ? "/lender/updateUserMetadata"
      : "/masterDashboard/updateUserMetadata";

    const payload: any = {
      metadata: { companyName },
      userID,
      identifier
    };

    return await Post<T>(url, token, payload, headers);
  }

  static async getKhojData({
    token,
    pageName,
    payload,
    isLenderDashboard,
    sourceEntityID
  }: ServiceMethodArgs<{
    payload: {
      refresh?: boolean;
      userId: string;
      lenderID: string;
      apiName?: string;
      userType?: string;
    };
    isLenderDashboard: boolean;
    sourceEntityID: string;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
      if (sourceEntityID) headers.append("source-entity-id", sourceEntityID);
    } else {
      return noHeadersResponse();
    }

    return await Post(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/third-party-data`,
      token,
      payload,
      headers
    );
  }

  static async getTaskList<T>({
    loanApplicationID,
    pageName,
    token,
    type,
    limit,
    page,
    resource
  }: {
    loanApplicationID: string;
    pageName: string;
    token: string;
    type?: string[];
    limit: number;
    page: number;
    resource?: string;
  }): Promise<FinBoxResponse<T>> {
    const headers = new Headers();

    if (pageName) {
      headers?.append("page-header", pageName);
    }

    const body = {
      types: type || [],
      loanApplicationID: loanApplicationID,
      limit: limit,
      page: page,
      resource
    } as any;

    return await Post<T>(`/lender/getTaskList`, token, body, headers);
  }

  static async getTaskListWithPayload<T>({
    token,
    pageName,
    payload,
    sourceEntityID,
    isLenderDashboard
  }: {
    token: string;
    pageName: string;
    payload: {
      types: string[];
      loanApplicationID: string;
      limit: number;
      page: number;
    };
    sourceEntityID: string;
    isLenderDashboard: boolean;
  }): Promise<FinBoxResponse<T>> {
    const headers = new Headers();

    if (pageName) {
      headers.append("page-header", pageName);
      if (sourceEntityID) headers.append("source-entity-id", sourceEntityID);
    } else return noHeadersResponse() as Promise<FinBoxResponse<T>>;

    return await Post<T>(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/getTaskList`,
      token,
      payload,
      headers
    );
  }

  static async getConfigData<T>({
    payload,
    token
  }: {
    payload: any;
    token: string;
  }) {
    const headers = new Headers();

    return await Post<T>(`/lender/fetch-graphql-data`, token, payload, headers);
  }

  static async getFormUIConfig<T>({
    payload,
    token,
    pageName,
    isPlatformDashboard
  }: {
    payload: {
      loanApplicationID: string;
      resourceName: "bcm_verification" | any;
      sectionName: "family_tree" | any;
      service: "lender_dashboard" | any;
      userID?: string;
      masterTaskID?: string;
      relevantTo?: string;
    };
    token: string;
    pageName: string;
    isPlatformDashboard?: boolean;
  }) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }

    return await Post<T>(
      `/${
        isPlatformDashboard ? "masterDashboard" : "lender"
      }/fetch-uibuilder-config`,
      token,
      payload,
      headers
    );
  }

  static async getUiConfig<T>({
    loanApplicationID,
    pageName,
    token,
    masterTaskID
  }: {
    loanApplicationID: string;
    pageName: string;
    token: string;
    masterTaskID: string;
  }) {
    const headers = new Headers();

    if (pageName) {
      headers?.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    const body = {
      resourceName: "lap_wf_task",
      service: "lender_dashboard",
      identifierType: "task",
      loanApplicationID: loanApplicationID,
      identifierID: masterTaskID,
      ...(useV2() && { programType: getStoredProgrammeType() })
    };

    return await Post<T>(`/lender/fetch-uiconfig`, token, body, headers);
  }

  static async doPennyDrop({
    token,
    pageName,
    payload
  }: ServiceMethodArgs<{
    payload: {
      loanApplicationID: string;
      ifscCode: string;
    };
  }>) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    else return noHeadersResponse();

    return await Post(`/lender/doPennyDrop`, token, payload, headers);
  }

  static async getWorkflowReqData({
    token,
    pageName,
    masterTaskID,
    currentStage,
    taskId,
    workflowInstanceId,
    isLenderPlatform,
    sourceEntityId,
    loanApplicationID,
    isLenderDashboard
  }: ServiceMethodArgs<{
    masterTaskID?: string;
    currentStage?: string;
    taskId?: string;
    workflowInstanceId?: string;
    isLenderPlatform?: boolean;
    sourceEntityId?: string;
    loanApplicationID: string;
    isLenderDashboard?: boolean;
  }>) {
    const headers = new Headers();

    if (pageName) {
      headers.append("page-header", pageName);
      headers?.append("source-entity-id", sourceEntityId as string);
    } else return noHeadersResponse();

    const searchQuery = new URLSearchParams();
    if (masterTaskID) searchQuery.append("masterTaskID", masterTaskID);
    if (currentStage) searchQuery.append("currentStage", currentStage);
    if (taskId) searchQuery.append("taskId", taskId);
    if (workflowInstanceId)
      searchQuery.append("workFlowInstanceId", workflowInstanceId);
    if (loanApplicationID)
      searchQuery.append("loanApplicationID", loanApplicationID);

    if (
      isLenderDashboard !== undefined &&
      typeof isLenderDashboard === "boolean"
    ) {
      return await Get(
        `/${
          isLenderDashboard ? "lender" : "masterDashboard"
        }/workflow-data?${searchQuery.toString()}`,
        token,
        headers
      );
    }

    return await Get(
      `/${
        isLenderPlatform ? "masterDashboard" : "lender"
      }/workflow-data?${searchQuery.toString()}`,
      token,
      headers
    );
  }

  static async addWorkflowReqData({
    token,
    pageName,
    payload,
    sourceEntityId,
    isLenderPlatform,
    isLenderDashboard
  }: ServiceMethodArgs<{
    loanApplicationID?: string;
    payload: any;
    sourceEntityId?: string;
    isLenderPlatform?: boolean;
    isLenderDashboard?: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
      if (sourceEntityId)
        headers.append("source-entity-id", sourceEntityId as string);
    } else return noHeadersResponse();

    if (
      isLenderDashboard !== undefined &&
      typeof isLenderDashboard === "boolean"
    ) {
      return await Post(
        `/${isLenderDashboard ? "lender" : "masterDashboard"}/workflow-action`,
        token,
        payload,
        headers
      );
    }

    return await Post(
      `/${!isLenderPlatform ? "masterDashboard" : "lender"}/workflow-action`,
      token,
      payload,
      headers
    );
  }

  static async getConfig({
    token,
    pageName,
    payload,
    isLenderDashboard
  }: ServiceMethodArgs<{ payload: any; isLenderDashboard: boolean }>) {
    const headers = new Headers();

    try {
      if (pageName) headers.append("page-header", pageName.trim());
    } catch (error) {
      console.error("Error setting headers:", error as Error);
      return noHeadersResponse();
    }

    return await Post(
      `${isLenderDashboard ? "/lender" : "/masterDashboard"}/fetch-uiconfig`,
      token,
      { ...payload, ...(useV2() && { programType: getStoredProgrammeType() }) },
      headers
    );
  }

  static async submitDataFromDashboard({
    token,
    payload,
    pageName,
    isLenderPlatform
  }: ServiceMethodArgs<{
    payload: any;
    pageName: string;
    isLenderPlatform: boolean;
  }>) {
    const headers = new Headers();

    try {
      if (pageName) headers.append("page-header", pageName);
    } catch (error) {
      console.error("Error setting headers:", error as Error);
      return noHeadersResponse();
    }

    return await Post(
      `/${isLenderPlatform ? "lender" : "masterDashboard"}/submit-form-data`,
      token,
      payload,
      headers
    );
  }

  static async submitLoanOfferData({
    token,
    payload,
    pageName,
    isLenderDashboard
  }: ServiceMethodArgs<{
    payload: any;
    pageName: string;
    isLenderDashboard: boolean;
  }>) {
    const headers = new Headers();

    try {
      if (pageName) headers.append("page-header", pageName);
    } catch (error) {
      console.error("Error setting headers:", error as Error);
      return noHeadersResponse();
    }

    return await Post(
      `/${
        isLenderDashboard ? "lender" : "masterDashboard"
      }/single-submit-loanoffer`,
      token,
      payload,
      headers
    );
  }

  static async validateDataFromDashboard({
    taskID,
    resourceName,
    userID,
    loanApplicationID,
    token,
    pageName,
    isLenderPlatform,
    data,
    extraFields
  }: ServiceMethodArgs<{
    taskID: string;
    resourceName: string;
    userID: string;
    loanApplicationID: string;
    isLenderPlatform?: boolean;
    data?: Record<string, unknown>;
    pageName: string;
    token: string;
    extraFields?: Record<string, unknown>;
  }>) {
    const headers = new Headers();

    try {
      if (pageName) headers.append("page-header", pageName);
    } catch (error) {
      console.error("Error setting headers:", error);
      return noHeadersResponse();
    }

    return await Post(
      `/${isLenderPlatform ? "lender" : "masterDashboard"}/validate-form-data`,
      token,
      {
        taskID,
        resourceName,
        userID,
        loanApplicationID,
        token,
        pageName,
        data,
        extraFields
      },
      headers
    );
  }

  static async fetchCategoryList({
    pageName,
    token,
    payload,
    isPlatformDashboard,
    sourceEntityID
  }: {
    pageName: string;
    token: string;
    payload: any;
    isPlatformDashboard?: boolean;
    sourceEntityID?: string;
  }) {
    const headers = new Headers();
    headers.append("page-header", pageName);
    if (sourceEntityID) headers.append("source-entity-id", sourceEntityID);

    return await Post(
      `/${
        isPlatformDashboard ? "masterDashboard" : "lender"
      }/generic-dropdown-list`,
      token,
      payload,
      headers
    );
  }

  // Insurance Api Calls
  static async getInsuranceList<T>({
    loanApplicationID,
    pageName,
    token,
    isLenderDashboard,
    sourceEntityID
  }: {
    loanApplicationID: string;
    pageName: string;
    token: string;
    isLenderDashboard: boolean;
    sourceEntityID?: string;
  }): Promise<FinBoxResponse<T> & { statusCode: number }> {
    const headers = new Headers();

    if (pageName) {
      headers?.append("page-header", pageName);
    }
    if (sourceEntityID) {
      headers?.append("source-entity-id", sourceEntityID);
    }
    return await Get<T>(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/insurance-config-data?loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }

  static async saveInsuranceSelection<T>({
    token,
    loanApplicationID,
    insuranceTypes,
    pageName,
    isLenderDashboard,
    sourceEntityID
  }: {
    token: string;
    loanApplicationID: string;
    insuranceTypes: string[];
    pageName: string;
    isLenderDashboard: boolean;
    sourceEntityID?: string;
  }): Promise<FinBoxResponse<T> & { statusCode: number }> {
    const headers = new Headers();

    try {
      if (pageName) headers.append("page-header", pageName);
      if (sourceEntityID) {
        headers?.append("source-entity-id", sourceEntityID);
      }
    } catch (error) {
      console.error("Error setting headers:", error);
      return noHeadersResponse();
    }

    return await Put<T>(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/save-insurance-selection`,
      token,
      {
        loanApplicationID,
        insuranceTypes
      },
      headers
    );
  }

  static async getActiveInsurances<T>({
    loanApplicationID,
    pageName,
    token,
    isLenderDashboard,
    sourceEntityID
  }: {
    loanApplicationID: string;
    pageName: string;
    token: string;
    isLenderDashboard: boolean;
    sourceEntityID?: string;
  }): Promise<FinBoxResponse<T> & { statusCode: number }> {
    const headers = new Headers();

    if (pageName) {
      headers?.append("page-header", pageName);
    }
    if (sourceEntityID) {
      headers?.append("source-entity-id", sourceEntityID);
    }
    return await Get<T>(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/get-active-insurances?loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }

  static async updateInsurance<T>({
    token,
    loanApplicationID,
    applicantUpdates,
    pageName,
    flowType,
    isLenderDashboard,
    sourceEntityID
  }: {
    token: string;
    loanApplicationID: string;
    applicantUpdates: string[];
    pageName: string;
    flowType: string;
    isLenderDashboard: boolean;
    sourceEntityID?: string;
  }): Promise<FinBoxResponse<T> & { statusCode: number }> {
    const headers = new Headers();

    try {
      if (pageName) headers.append("page-header", pageName);
      if (sourceEntityID) {
        headers?.append("source-entity-id", sourceEntityID);
      }
    } catch (error) {
      console.error("Error setting headers:", error);
      return noHeadersResponse();
    }

    return await Post<T>(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/update-insurance`,
      token,
      {
        loanApplicationID,
        applicantUpdates,
        flowType
      },
      headers
    );
  }

  static async rejectInsurance<T>({
    token,
    loanApplicationID,
    pageName,
    isLenderDashboard,
    reason,
    sourceEntityID
  }: {
    token: string;
    loanApplicationID: string;
    pageName: string;
    isLenderDashboard: boolean;
    reason: string;
    sourceEntityID?: string;
  }): Promise<FinBoxResponse<T> & { statusCode: number }> {
    const headers = new Headers();
    try {
      if (pageName) headers.append("page-header", pageName);
      if (sourceEntityID) {
        headers?.append("source-entity-id", sourceEntityID);
      }
    } catch (error) {
      console.error("Error setting headers:", error);
    }
    return await Post<T>(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/reject-insurance`,
      token,
      {
        loanApplicationID,
        rejectionReason: reason
      },
      headers
    );
  }

  static async finalizeInsurance<T>({
    token,
    loanApplicationID,
    pageName,
    isLenderDashboard,
    sourceEntityID
  }: {
    token: string;
    loanApplicationID: string;
    pageName: string;
    isLenderDashboard: boolean;
    sourceEntityID?: string;
  }): Promise<FinBoxResponse<T> & { statusCode: number }> {
    const headers = new Headers();

    try {
      if (pageName) headers.append("page-header", pageName);
      if (sourceEntityID) {
        headers?.append("source-entity-id", sourceEntityID);
      }
    } catch (error) {
      console.error("Error setting headers:", error);
      return noHeadersResponse();
    }

    return await Post<T>(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/finalize-insurances`,
      token,
      {
        loanApplicationID
      },
      headers
    );
  }

  // Disbursement
  static async getDisbursementDetails({
    token,
    loanApplicationID,
    pageName,
    disbursementAmount,
    trancheID,
    disbursementDate
  }: ServiceMethodArgs<{
    token: string;
    loanApplicationID: string;
    pageName: string;
    disbursementAmount?: number;
    trancheID?: string;
    disbursementDate?: string;
  }>) {
    const headers = new Headers();

    try {
      if (pageName) headers.append("page-header", pageName);
    } catch (error) {
      console.error("Error setting headers:", error);
      return noHeadersResponse();
    }

    const url =
      `/lender/disbursement-details?loanApplicationID=${loanApplicationID}` +
      (disbursementAmount !== undefined
        ? `&requestedAmount=${disbursementAmount}`
        : "") +
      (trancheID ? `&trancheID=${trancheID}` : "") +
      (disbursementDate !== undefined
        ? `&disbursementDate=${disbursementDate}`
        : "");
    return await Get(url, token, headers);
  }

  static async getActiveDisbursements({
    loanApplicationID,
    pageName,
    token
  }: ServiceMethodArgs<{
    loanApplicationID: string;
    pageName: string;
    token: string;
  }>) {
    const headers = new Headers();

    try {
      if (pageName) headers.append("page-header", pageName);
    } catch (error) {
      console.error("Error setting headers:", error);
      return noHeadersResponse();
    }

    return await Get(
      `/lender/fetch-disbursements?loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }

  static async createDisbursement({
    token,
    pageName,
    payload
  }: {
    token: string;
    pageName: string;
    payload: {
      paymentAdvice: PaymentAdviceType.SINGLE | PaymentAdviceType.MULTIPLE;
    };
  }) {
    const headers = new Headers();

    try {
      if (pageName) headers.append("page-header", pageName);
    } catch (error) {
      console.error("Error setting headers:", error);
      return noHeadersResponse();
    }

    return await Post(`/lender/create-disbursement`, token, payload, headers);
  }

  static async updateDisbursement({
    token,
    pageName,
    payload
  }: {
    token: string;
    pageName: string;
    payload: {
      paymentAdvice: PaymentAdviceType.SINGLE | PaymentAdviceType.MULTIPLE;
    };
  }) {
    const headers = new Headers();

    try {
      if (pageName) headers.append("page-header", pageName);
    } catch (error) {
      console.error("Error setting headers:", error);
      return noHeadersResponse();
    }

    return await Put(`/lender/update-disbursement`, token, payload, headers);
  }

  static async deleteDisbursement({
    token,
    pageName,
    id,
    isLenderDashboard
  }: ServiceMethodArgs<{
    token: string;
    pageName: string;
    id: string;
    isLenderDashboard: boolean;
  }>) {
    const headers = new Headers();

    try {
      if (pageName) {
        headers.append("page-header", pageName);
      }
    } catch (error) {
      console.error("Error setting headers:", error);
      return noHeadersResponse();
    }

    return await Delete(
      `${
        isLenderDashboard ? "/lender" : "/masterDashboard"
      }/delete-disbursement?id=${id}`,
      token,
      {},
      headers
    );
  }

  static async updateDisbursementStatus({
    token,
    pageName,
    payload,
    isLenderDashboard
  }: ServiceMethodArgs<{
    token: string;
    pageName: string;
    payload: any;
    isLenderDashboard: boolean;
  }>) {
    const headers = new Headers();

    try {
      if (pageName) headers.append("page-header", pageName);
    } catch (error) {
      console.error("Error setting headers:", error);
      return noHeadersResponse();
    }

    return await Post(
      `${isLenderDashboard ? "/lender" : "/masterDashboard"}/workflow-action`,
      token,
      payload,
      headers
    );
  }

  static async reUploadAdditionalDocs<T>({
    token,
    pageName,
    payload,
    isLenderDashboard
  }: ServiceMethodArgs<{
    token: string;
    pageName: string;
    payload: Record<string, unknown>;
    isLenderDashboard: boolean;
  }>): Promise<T> {
    const headers = new Headers();

    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Post(
      `/${
        isLenderDashboard ? "lender" : "masterDashboard"
      }/additional-document-re-submission`,
      token,
      payload,
      headers
    );
  }

  static async fetchInsurancePremimum<T>({
    token,
    pageName,
    payload,
    isLenderDashboard,
    sourceEntityID
  }: {
    token: string;
    pageName: string;
    payload: {
      fetchPremiumRequests: Array<{
        userID: string;
        insuranceID: string;
        sumAssured: number;
        tenure: number;
      }>;
      loanApplicationID: string;
    };
    isLenderDashboard: boolean;
    sourceEntityID?: string;
  }): Promise<FinBoxResponse<T> & { statusCode: number }> {
    const headers = new Headers();

    if (pageName)
      headers.append("page-header", "Loan Application Insurance Tab");

    if (sourceEntityID) {
      headers?.append("source-entity-id", sourceEntityID);
    }

    return await Post<T>(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/fetch-insurance-premium`,
      token,
      payload,
      headers
    );
  }

  static async triggerHunterCheck<T>({
    token,
    pageName,
    loanApplicationID,
    userID,
    isLenderPlatform
  }: ServiceMethodArgs<{
    token: string;
    pageName: string;
    loanApplicationID: string;
    userID: string;
    isLenderPlatform?: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    const payload = {
      loanApplicationID,
      userID
    } as any;

    return await Post<T>(
      `${isLenderPlatform ? "/lender" : "/masterDashboard"}/hunter-check`,
      token,
      payload,
      headers
    );
  }

  static async generateWebLink({
    token,
    pageName,
    payload,
    isLenderPlatform
  }: ServiceMethodArgs<{
    token: string;
    pageName: string;
    payload: any;
    isLenderPlatform: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    else return noHeadersResponse();

    const searchParams = new URLSearchParams();

    searchParams.append("userID", payload.userID);
    searchParams.append("redirectURL", window.location.href);

    const resp = await Get<{ url: string }>(
      `/${
        isLenderPlatform ? "lender" : "masterDashboard"
      }/generateWebLink?${searchParams.toString()}`,
      token,
      headers
    );

    return resp.status && resp.data ? resp.data?.url : "";
  }

  static async generateJourneyWebLink<T>({
    token,
    pageName,
    payload,
    isPlatformDashboard
  }: ServiceMethodArgs<{
    token: string;
    pageName: string;
    payload: {
      userID: string;
      redirectURL: string;
    };
    isPlatformDashboard?: boolean;
  }>): Promise<FinBoxResponse<T> & { statusCode: number }> {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    else return noHeadersResponse() as any;

    return (await Post<{ url: string }>(
      `/${
        isPlatformDashboard ? "masterDashboard" : "lender"
      }/generate-journey-web-link`,
      token,
      payload,
      headers
    )) as any;
  }

  static async offlineEnach<T>({
    token,
    pageName,
    payload
  }: ServiceMethodArgs<{
    token: string;
    pageName: string;
    payload: {
      loanApplicationID: string;
      umrnNumber: string;
    };
  }>) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    else return noHeadersResponse();

    return await Post<T>("/lender/offline-enach", token, payload, headers);
  }

  static async eSignAttempt({
    token,
    pageName,
    payload
  }: ServiceMethodArgs<{
    token: string;
    pageName: string;
    payload: any;
  }>) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    else return noHeadersResponse();

    const searchParams = new URLSearchParams();

    searchParams.append("loanApplicationID", payload.loanApplicationID);

    return await Get<{
      url: string;
      esignStatus: number;
      enableButton: boolean;
    }>(`/lender/e-sign-attempt?${searchParams.toString()}`, token, headers);
  }

  static async uploadLenderDoc<T>({
    token,
    pageName,
    payload
  }: ServiceMethodArgs<{
    token: string;
    pageName: string;
    payload: {
      mediaIDs: string[];
      comment: string;
      loanApplicationID: string;
      mediaIdsToDocumentIds: Record<string, string>;
    };
  }>) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    else return noHeadersResponse();

    return await Post<T>("/lender/uploadDocs", token, payload, headers);
  }

  static async postESignAttempt<T>({
    token,
    pageName,
    payload
  }: ServiceMethodArgs<{
    token: string;
    pageName: string;
    payload: {
      loanApplicationID: string;
      documentIDs: string[];
    };
  }>) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    else return noHeadersResponse();

    return await Post<T>("/lender/e-sign-attempt", token, payload, headers);
  }

  static async sendTemporalSignal<T>({
    token,
    pageName,
    payload,
    isLenderPlatform
  }: ServiceMethodArgs<{
    token: string;
    pageName: string;
    payload: {
      loanApplicationID: string;
      resourceName: "penny-drop" | "e-sign";
      mode: "offline" | "online";
    };
    isLenderPlatform: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    else return noHeadersResponse();

    return await Post<T>(
      `${
        isLenderPlatform ? "/lender" : "masterDashboard"
      }/temporal-signal/send`,
      token,
      payload,
      headers
    );
  }

  static async sendEmailOtp({
    token,
    pageName,
    payload,
    isLenderPlatform
  }: ServiceMethodArgs<{
    payload: {
      userId: string;
      loanApplicationId: string;
      otpType: string;
      email: string;
    };
    isLenderPlatform: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Post(
      `/${isLenderPlatform ? "lender" : "masterDashboard"}/generate-email-otp`,
      token,
      payload,
      headers
    );
  }

  static async verifyEmailOtp({
    token,
    pageName,
    payload,
    isLenderPlatform
  }: ServiceMethodArgs<{
    payload: {
      userId: string;
      loanApplicationId: string;
      otpType: string;
      otp: number;
    };
    isLenderPlatform: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Post(
      `/${isLenderPlatform ? "lender" : "masterDashboard"}/verify-email-otp`,
      token,
      payload,
      headers
    );
  }

  static async genericDropdownList({
    pageName,
    token,
    payload,
    isLenderDashboard,
    sourceEntityID
  }: {
    pageName: string;
    token: string;
    payload: any;
    isLenderDashboard: boolean;
    sourceEntityID?: string;
  }) {
    const headers = new Headers();
    headers.append("page-header", pageName);
    if (sourceEntityID) {
      headers.append("source-entity-id", sourceEntityID);
    }

    return await Post(
      `/${
        isLenderDashboard ? "lender" : "masterDashboard"
      }/generic-dropdown-list`,
      token,
      payload,
      headers
    );
  }

  static async getBankConnectData({
    token,
    loanApplicationID,
    pageName,
    userID,
    isLenderDashboard
  }: {
    token: string;
    loanApplicationID: string;
    pageName: string;
    userID: string;
    isLenderDashboard?: boolean;
  }) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    const params = new URLSearchParams();
    params.append("loanApplicationID", loanApplicationID);

    if (userID) params.append("userID", userID);

    return await Get(
      `/${
        isLenderDashboard ? "lender" : "masterDashboard"
      }/getBankStatements?${params.toString()}`,
      token,
      headers
    );
  }

  static async getBankConnectSession({
    userID,
    token,
    pageName,
    sourceEntityID,
    isLenderDashboard
  }: ServiceMethodArgs<{
    userID: string;
    sourceEntityID: string;
    isLenderDashboard: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    headers.append("source-entity-id", sourceEntityID);
    return await Get(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/bank-connect-session?userID=${userID}`,
      token,
      headers
    );
  }

  static async triggerBankConnectProcessing({
    userID,
    sessionID,
    token,
    pageName,
    sourceEntityID,
    isLenderDashboard
  }: ServiceMethodArgs<{
    userID: string;
    sessionID: string;
    sourceEntityID: string;
    isLenderDashboard: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    headers.append("source-entity-id", sourceEntityID);
    return await Post(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/trigger-bank-connect-processing`,
      token,
      {
        userID,
        sessionID
      },
      headers
    );
  }

  static async getBankConnectPollingStatus({
    bankConnectDetailsID,
    token,
    pageName,
    sourceEntityID,
    isLenderDashboard
  }: ServiceMethodArgs<{
    bankConnectDetailsID: string;
    sourceEntityID: string;
    isLenderDashboard: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    headers.append("source-entity-id", sourceEntityID);
    return await Get(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/bank-connect-details?bankConnectDetailsID=${bankConnectDetailsID}`,
      token,
      headers
    );
  }

  static async getBankConnectReports({
    loanApplicationID,
    token,
    pageName,
    userID,
    isLenderDashboard
  }: {
    loanApplicationID?: string;
    token: string;
    pageName: string;
    userID: string;
    isLenderDashboard?: boolean;
  }) {
    const urlParams = new URLSearchParams();
    if (loanApplicationID)
      urlParams.append("loanApplicationID", loanApplicationID);
    urlParams.append("userID", userID);

    const headers = new Headers();
    headers.append("page-header", pageName);

    return await Get(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/getBankConnectReportV2?${urlParams.toString()}`,
      token,
      headers
    );
  }

  static async getSimulatedBankConnectReports<T>({
    loanApplicationID,
    simulationID,
    token,
    pageName,
    userID,
    isLenderDashboard
  }: {
    loanApplicationID: string;
    simulationID: string;
    token: string;
    pageName: string;
    userID: string;
    isLenderDashboard?: boolean;
  }): Promise<FinBoxResponse<T>> {
    const urlParams = new URLSearchParams();
    urlParams.append("loanApplicationID", loanApplicationID);
    urlParams.append("simulationID", simulationID);
    urlParams.append("userID", userID);

    const headers = new Headers();
    headers.append("page-header", pageName);

    return await Get<T>(
      `/${
        isLenderDashboard ? "lender" : "masterDashboard"
      }/getSimulatedBankConnectReport?${urlParams.toString()}`,
      token,
      headers
    );
  }

  static async getRefreshedBankStatements({
    loanApplicationID,
    token,
    pageName,
    isLenderDashboard
  }: ServiceMethodArgs<{
    loanApplicationID: string;
    isLenderDashboard?: boolean;
  }>) {
    const headers = new Headers();
    console.log(pageName);
    if (pageName) {
      headers.append("page-header", pageName);
    }
    return await Get(
      `/${
        isLenderDashboard ? "lender" : "masterDashboard"
      }/refetchStatements?loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }

  static async upsertMetadata({
    token,
    pageName,
    payload,
    isLenderDashboard,
    isMFL_LAP
  }: ServiceMethodArgs<{
    payload: {
      userID: string;
      identifier: "references" | "family_tree" | "collateral_ownership";
      loanApplicationID?: string;
      masterTaskID: string;
      metadata: Array<{
        id?: string;
        name: string;
        type?: string;
        relationship?: string;
        address: string;
        mobile_number: string;
        status: "ACTIVE" | "INACTIVE";
        percentage?: number;
      }>;
    };
    isLenderDashboard: boolean;
    isMFL_LAP: boolean;
    sourceEntityID?: string;
    pageName: string;
    token: string;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      console.error("Error setting headers: pageName is required");
      return noHeadersResponse();
    }

    return await Post(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/${
        isMFL_LAP ? "upsert-user-metadata" : "upsert-metadata"
      }`,
      token,
      payload,
      headers
    );
  }

  static async getBureauData({
    loanID,
    token,
    pageName,
    isLenderDashboard,
    userID
  }: {
    loanID: string;
    token: string;
    pageName: string;
    userID: string;
    isLenderDashboard?: boolean;
  }) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    return await Get<FinBoxResponse<BureauDataInterface>>(
      `/${
        isLenderDashboard ? "lender" : "masterDashboard"
      }/getBureauReports?loanApplicationID=${loanID}&userID=${userID}`,
      token,
      headers
    );
  }

  static async getCAMReports({
    loanID,
    token,
    pageName,
    isLenderDashboard,
    userID
  }: {
    loanID: string;
    token: string;
    pageName: string;
    isLenderDashboard?: boolean;
    userID: string;
  }) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Get<FinBoxResponse<BureauDataInterface>>(
      `/${
        isLenderDashboard ? "lender" : "masterDashboard"
      }/getCAMReports?loanApplicationID=${loanID}&userID=${userID}`,
      token,
      headers
    );
  }

  static async getBREWorkflowTimeline<T>({
    serviceRequestID,
    token,
    pageName,
    sourceEntityID
  }: {
    serviceRequestID: string;
    token: string;
    pageName: string;
    sourceEntityID: string;
  }) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    headers.append("source-entity-id", sourceEntityID);
    return await Get<T>(
      `/masterDashboard/getBREWorkflowTimeline?serviceRequestID=${serviceRequestID}`,
      token,
      headers
    );
  }

  static async validateGstin<T>({
    token,
    pageName,
    payload,
    isLenderDashboard,
    sourceEntityID
  }: ServiceMethodArgs<{
    payload: any;
    isLenderDashboard?: boolean;
    sourceEntityID?: string;
  }>): Promise<FinBoxResponse<T>> {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);

    if (sourceEntityID) headers.append("source-entity-id", sourceEntityID);

    return await Post<T>(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/validateGstin`,
      token,
      payload,
      headers
    );
  }

  static async updateUdyamDetails({
    payload,
    token,
    pageName,
    isLenderDashboard,
    sourceEntityID
  }: ServiceMethodArgs<{
    token: string;
    pageName: string;
    payload: {
      loanApplicationID: string;
      udyamNumber: string;
      userID: string;
    };
    isLenderDashboard?: boolean;
    sourceEntityID?: string;
  }>) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    else return noHeadersResponse();

    if (sourceEntityID) headers.append("source-entity-id", sourceEntityID);

    return await Post(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/updateUdyamDetails`,
      token,
      payload,
      headers
    );
  }

  static async getGSTData({
    token,
    loanApplicationID,
    pageName,
    userID,
    isLenderDashboard,
    sourceEntityID
  }: {
    token: string;
    loanApplicationID: string;
    pageName: string;
    userID: string;
    isLenderDashboard?: boolean;
    sourceEntityID?: string;
  }) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    if (sourceEntityID) headers.append("source-entity-id", sourceEntityID);

    const params = new URLSearchParams();
    params.append("loanApplicationID", loanApplicationID);
    if (userID) params.append("userID", userID);

    return await Get(
      `/${
        isLenderDashboard ? "lender" : "masterDashboard"
      }/getGSTDetails?${params.toString()}`,
      token,
      headers
    );
  }

  static async getCibilReport({
    loanID,
    token,
    pageName,
    isLenderDashboard,
    sourceEntityID
  }: {
    loanID: string;
    token: string;
    pageName: string;
    isLenderDashboard?: boolean;
    sourceEntityID?: string;
  }) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }
    if (sourceEntityID) {
      headers.append("source-entity-id", sourceEntityID);
    }
    return await Get<CibilDataInterface[]>(
      `/${
        isLenderDashboard ? "lender" : "masterDashboard"
      }/getCibilReport?loanApplicationID=${loanID}`,
      token,
      headers
    );
  }

  static async retriggerDisbursement<T>({
    token,
    pageName,
    payload,
    isLenderDashboard,
    sourceEntityID
  }: ServiceMethodArgs<{
    token: string;
    pageName: string;
    payload: { disbursementRequestID: string };
    isLenderDashboard: boolean;
    sourceEntityID?: string;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else return noHeadersResponse();

    return await Post<T>(
      `/${
        isLenderDashboard ? "lender" : "masterDashboard"
      }/retrigger-disbursement`,
      token,
      payload,
      headers
    );
  }

  static async checkRetriggerStatus({
    token,
    pageName,
    payload,
    isLenderDashboard
  }: ServiceMethodArgs<{
    token: string;
    pageName: string;
    payload: { disbursementRequestID: string };
    isLenderDashboard: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else return noHeadersResponse();

    return await Post<T>(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/retrigger-status`,
      token,
      payload,
      headers
    );
  }

  static async addUser({
    token,
    pageName,
    payload,
    sourceEntityID
  }: ServiceMethodArgs<{ payload: any; sourceEntityID: string }>) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    else return noHeadersResponse();

    headers.append("source-entity-id", sourceEntityID);

    return await Put(`/masterDashboard/addUser`, token, payload, headers);
  }

  static async getExistingApplication({
    token,
    pageName,
    payload,
    isLenderDashboard
  }: ServiceMethodArgs<{
    payload: { mobile: string; sourceEntityID: string };
    isLenderDashboard: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) headers.append("page-header", pageName);
    else return noHeadersResponse();

    return await Post(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/getExistingApplication`,
      token,
      payload,
      headers
    );
  }

  static async getPreApprovedOffers({
    token,
    pageName,
    payload
  }: ServiceMethodArgs<{ payload: any }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    const urlSearchParams = new URLSearchParams();
    urlSearchParams.append("mobile", payload?.mobile);
    urlSearchParams.append("pan", payload?.pan);

    return await Get(
      `/masterDashboard/getPreApprovedOffers?${urlSearchParams.toString()}`,
      token,
      headers
    );
  }

  static async getPolicyUiConfig<T>({
    token,
    loanID,
    isPlatformDashboard,
    pageName,
    isLenderDashboard
  }: ServiceMethodArgs<{
    token: string;
    loanID: string;
    isPlatformDashboard?: boolean;
    pageName?: string;
    isLenderDashboard?: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    let url = "";

    if (
      isLenderDashboard !== undefined &&
      typeof isLenderDashboard === "boolean"
    ) {
      url = `/${
        isLenderDashboard ? "lender" : "masterDashboard"
      }/fetch-ui-permissions`;
    } else {
      url = `/${
        isPlatformDashboard ? "masterDashboard" : "lender"
      }/fetch-ui-permissions`;
    }

    const payload: any = {
      loanApplicationID: loanID
    };

    return await Post<T>(url, token, payload, headers);
  }

  /**
   * Fetches tabs + sidebar ui-config for a details page (loan application or
   * leads). `resourceName` defaults to "dashboard_info" (loan applications);
   * pass UI_CONFIG_RESOURCE_NAMES.dashboardLeads for the Leads details page.
   * loanID/userID are loan-application-specific and omitted from the payload
   * entirely when not provided (leads don't need them).
   */
  static async getTabsFromUIConfig<T>({
    loanID,
    userID,
    token,
    isLenderDashboard,
    uniqueID,
    pageName,
    resourceName = "dashboard_info"
  }: ServiceMethodArgs<{
    loanID?: string;
    userID?: string;
    token: string;
    uniqueID: string;
    isLenderDashboard?: boolean;
    resourceName?: string;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    const url = `/${isLenderDashboard ? "lender" : "masterDashboard"}/fetch-uiconfig`;

    const payload: any = {
      loanApplicationID: loanID,
      programType: getStoredProgrammeType(),
      resourceName,
      programType: getStoredProgrammeType(),
      service: `${isLenderDashboard ? "lender_dashboard" : "platform_dashboard"}`,
      sub_programme_type: "",
      unique_id: uniqueID,
      userID: userID,
      ...(useV2() && { programType: getStoredProgrammeType() }),
      ...(loanID ? { loanApplicationID: loanID } : {}),
      ...(userID ? { userID } : {})
    };

    return await Post<T>(url, token, payload, headers);
  }

  static async getDocuments({
    token,
    pageName,
    payload,
    isLenderDashboard,
    sourceEntityID
  }: ServiceMethodArgs<{
    token: string;
    pageName: string;
    payload: any;
    isLenderDashboard: boolean;
    sourceEntityID?: string;
  }>) {
    const headers = new Headers();
    if (sourceEntityID) {
      headers.append("source-entity-id", sourceEntityID);
    }
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    return await Post(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/get-documents`,
      token,
      payload,
      headers
    );
  }

  static async sendSessionLinkToCustomer({
    token,
    pageName,
    payload,
    sourceEntityID
  }: ServiceMethodArgs<{ payload: any; sourceEntityID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    headers.append("source-entity-id", sourceEntityID);

    return await Post(
      `/masterDashboard/sendSessionLinkToCustomer`,
      token,
      payload,
      headers
    );
  }

  static async downloadCIBILReport({
    token,
    pageName,
    loanApplicationID,
    isLenderDashboard
  }: ServiceMethodArgs<{
    loanApplicationID: string;
    isLenderDashboard: boolean;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    }

    return await Get(
      `/${
        isLenderDashboard ? "lender" : "masterDashboard"
      }/downloadCIBILReport?loanApplicationID=${loanApplicationID}`,
      token,
      headers
    );
  }

  static async getUnderwriting({
    token,
    pageName,
    loanApplicationID,
    isLenderDashboard,
    sourceEntityID,
    userID
  }: ServiceMethodArgs<{
    loanApplicationID?: string;
    isLenderDashboard: boolean;
    pageName: string;
    sourceEntityID?: string;
    userID?: string;
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }

    let url;
    if (isLenderDashboard) url = `/lender/getUnderwriting`;
    else url = `/masterDashboard/getUnderwriting`;

    if (sourceEntityID) headers.append("source-entity-id", sourceEntityID);

    const queryString = new URLSearchParams();
    if (loanApplicationID)
      queryString.append("loanApplicationID", loanApplicationID);
    if (userID) queryString.append("userID", userID);

    return await Get<FinBoxResponse<UnderwritingResponse[]>>(
      `${url}?${queryString.toString()}`,
      token,
      headers
    );
  }

  static async reRunLastPolicy({
    token,
    pageName,
    loanApplicationID
  }: ServiceMethodArgs<{ loanApplicationID: string }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    return await Post<FinBoxResponse<{ msg: string }>>(
      `/lender/rerunLastPolicy`,
      token,
      { loanApplicationID },
      headers
    );
  }

  static async sentinelPolicyLink({
    token,
    pageName,
    payload
  }: ServiceMethodArgs<{
    payload: {
      userID: string;
      evaluationID: string;
      loanApplicationID: string;
    };
  }>) {
    const headers = new Headers();
    if (pageName) {
      headers.append("page-header", pageName);
    } else {
      return noHeadersResponse();
    }
    const queryString = new URLSearchParams({});
    queryString.append("userID", payload.userID);
    queryString.append("evaluationID", payload.evaluationID);
    queryString.append("loanApplicationID", payload.loanApplicationID);

    return await Get(
      `/lender/sentinelPolicyLink?${queryString}`,
      token,
      headers
    );
  }

  static async getDatadogDashboardUrl({
    token,
    pageName,
    sourceEntityID,
    isLenderDashboard
  }: ServiceMethodArgs<{
    sourceEntityID: string;
    isLenderDashboard: boolean;
  }>) {
    const headers = new Headers();
    if (!pageName) {
      return noHeadersResponse();
    }
    headers.append("page-header", pageName);
    const params = new URLSearchParams();
    params.append("sourceEntityID", sourceEntityID);

    return await Get<{ links: Array<{ embedLink: string }> }>(
      `${isLenderDashboard ? "lender" : "masterDashboard"}//getDatadogDashboardURL?${params.toString()}`,
      token,
      headers
    );
  }

  static async getAnalyticsUrl({
    token,
    pageName,
    isLenderDashboard
  }: ServiceMethodArgs<{ isLenderDashboard: boolean }>) {
    const headers = new Headers();
    if (!pageName) {
      return noHeadersResponse();
    }
    headers.append("page-header", pageName);

    return await Get<{ links: Array<{ embedLink: string }> }>(
      `/${isLenderDashboard ? "lender" : "masterDashboard"}/analytics/url`,
      token,
      headers
    );
  }
}
